import React, { FunctionComponent, lazy, Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { useAuth, useGlobalBanner } from '@/contexts';
import { identifyUser } from '@/analytics';
import { useClient, useWindowResize } from '@/hooks';
import { useIntercom, useIntercomAutoOpen } from '@/hooks/useIntercom';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { STOREFRONT_COMPACT_MAX_WIDTH } from '@/constants/layout';
import { useDownPaymentValidation } from '@/hooks/useDownPaymentValidation';
import { useMaxBookingDateValidation } from '@/hooks/useMaxBookingDateValidation';
import { useFreeTrialValidation } from '@/hooks/useFreeTrialValidation';
import {
  AgoraLinkTypeE,
  GiftCardTemplateI,
  GiftCardTemplateStatusE,
  SupportedCountriesE,
  VendorHistoryStatusE,
  WalletTypeE
} from '@/types/cyclone/models';
import classNames from 'clsx';
import { Header } from '../Header';
import { GlobalBanner } from '../GlobalBanner';
import { RailContainer } from '../Rail';
import { FunctionSearchDialog } from '@/features/function-search';
import { BillingAlertProvider, useBillingAlert } from '@/features/billing-alert/BillingAlertContext';
import type { BannerAction } from '@/contexts/GlobalBannerContext';
import {
  useWaPauseBanner,
  WaPauseBanner,
  WA_PAUSE_BANNER_QUERY_ROOT
} from '@/features/wa-pause-banner/WaPauseBanner';

const Footer = lazy(() => import('../Footer/Footer'));
// Anuncios in-app: lazy para no sumarle nada al bundle eager — el 99% de las veces el
// claim devuelve null y no se monta nada.
const AnnouncementsHost = lazy(() => import('@/features/announcements/AnnouncementsHost'));
const ChecklistReturnBanner = lazy(() => import('../ChecklistReturnBanner/ChecklistReturnBanner'));

interface LayoutPropsI {
  children?: React.ReactNode;
}

interface BillingBannerState {
  show: boolean;
  type: 'warning' | 'error';
  message: string;
  action?: BannerAction;
}

// Queries de las que depende el banner además de la sesión (alerta de cobro, salud
// del negocio, gift cards huérfanas, WhatsApp pausado).
const BANNER_QUERY_ROOTS = new Set([
  'billing-alert',
  'vendor-health',
  'gift-card-templates',
  WA_PAUSE_BANNER_QUERY_ROOT
]);
// Margen para que arranquen las queries que se habilitan recién con la sesión
// (el controlador de la alerta de cobro es lazy) antes de dar el banner por resuelto.
const BANNER_SETTLE_MS = 400;
// Tope: una query colgada no puede dejar el lugar reservado para siempre.
const BANNER_SETTLE_MAX_MS = 5000;

/**
 * El banner se dibuja desde el render, pero recién se sabe si corresponde cuando
 * llegaron la sesión y sus queries. Hasta entonces `false`: GlobalBanner reserva el
 * alto que tuvo en la visita anterior en vez de empujar toda la app al aparecer.
 */
const useBannerSettled = (hasSession: boolean): boolean => {
  const fetching = useIsFetching({ predicate: (q) => BANNER_QUERY_ROOTS.has(String(q.queryKey[0])) });
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (settled || !hasSession || fetching > 0) return;
    const timer = window.setTimeout(() => setSettled(true), BANNER_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [settled, hasSession, fetching]);

  useEffect(() => {
    if (settled || !hasSession) return;
    const timer = window.setTimeout(() => setSettled(true), BANNER_SETTLE_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [settled, hasSession]);

  return settled;
};

// Intercom solo con sesión: la carcasa ahora se monta antes de que Auth0 confirme
// quién es, y un `boot` en ese momento saldría como visitante anónimo.
const IntercomBridge: FunctionComponent<{ display: boolean }> = ({ display }) => {
  useIntercom(display);
  // Auto-open Intercom if URL contains ic_message or ic_open params
  useIntercomAutoOpen();
  return null;
};

const LayoutShell: FunctionComponent<LayoutPropsI> = ({ children }) => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { client } = useClient();
  const { bannerState } = useGlobalBanner();
  const [billingAlert, billingAlertBanner, openBillingAlert] = useBillingAlert();
  const waPauseBanner = useWaPauseBanner();
  const { shouldShowBanner } = useDownPaymentValidation();
  const { isMobileSize } = useWindowResize();
  // Una sola navegación montada por ancho: el riel (y el pie) desde `lg`, la barra superior
  // con su campana y su menú por debajo. Antes se montaban las dos y el CSS escondía una,
  // que igual seguía armando sus ítems, su campana y sus escuchas.
  const isDesktop = useIsDesktop();
  // Mi página has its own dense editor chrome. It uses the compact/full-screen
  // experience through tablet widths (≤1279px), so the global shell must follow the
  // same breakpoint instead of taking 72px away from a desktop editor that just mounted.
  const { isMobileSize: isStorefrontCompactSize } = useWindowResize(STOREFRONT_COMPACT_MAX_WIDTH);
  const maxBookingDateValidation = useMaxBookingDateValidation();
  const freeTrialValidation = useFreeTrialValidation();
  // De la ubicación del router y no de window.location: la carcasa ahora persiste
  // entre rutas y durante una navegación pendiente window.location ya es la nueva.
  const { pathname: path, search } = useLocation();
  const isAgenda = path.includes('/agenda');
  const isMiPagina = path.includes('/mi-pagina');
  // Compartir horarios es una pantalla, no un documento: mide 100dvh y trae su propio
  // pie pegado. El <Footer> del shell le agregaría scroll de documento abajo de todo.
  const isSharingStudio = path.includes('/contenido-ig/posteo') || path.includes('/compartir-horarios');
  const isReturningToChecklist = new URLSearchParams(search).get('from') === 'checklist';
  const isOnboardingOrSetup =
    path.includes('/onboarding') || path.includes('/setup') || path.includes('/bienvenido');

  // Gift cards are Mercado Pago-only: the public storefront gates visibility on the
  // actual MP wallet (cyclone → vendorCanSellGiftCards), not the `payment_gateways`
  // flag. A vendor can have active templates that simply never render — and never
  // realise it. Only fetch when they CAN'T sell (no usable wallet); vendors with MP
  // need no banner, so they pay no query. Shared queryKey dedupes with the panel.
  const mpWallet = session?.vendor?.wallet_mercado_pago;
  const canSellGiftCards = !!mpWallet && !mpWallet.needs_reconnection;
  // Gift cards exist only in MP countries (AR/UY); skip the query everywhere else.
  const isMpCountry =
    session?.vendor?.country === SupportedCountriesE.ARGENTINA ||
    session?.vendor?.country === SupportedCountriesE.URUGUAY;
  const { data: giftCardTemplatesData } = useQuery({
    queryKey: ['gift-card-templates'],
    queryFn: () =>
      client<{ items: GiftCardTemplateI[] }>('me/vendor/gift-cards/templates', 'GET', {
        isAuthRequired: true
      }),
    enabled: isMpCountry && !canSellGiftCards && !!session?.vendor?.is_onboarding_finished,
    retry: false,
    refetchOnWindowFocus: false
  });
  const hasOrphanedGiftCards = (giftCardTemplatesData?.items || []).some(
    (t) => t.status === GiftCardTemplateStatusE.ACTIVE
  );

  // Catálogo de productos "huérfano": add-on pago activo + productos activos, pero
  // el botón de Productos no aparece en la página (link inexistente u oculto). El
  // backend ya auto-expone el link al crear/activar productos y al activar el add-on,
  // así que este caso solo queda vivo si el vendor ocultó el botón a mano en el
  // editor de Mi Página. Mismo patrón que las gift cards huérfanas de arriba.
  const catalogAddonActive = session?.active_plan?.catalog_products_enabled === true;
  const hasActiveProducts = (session?.count_active_products ?? 0) > 0;
  const productLink = session?.vendor?.vendor_links?.find(
    (l) => l.agora_link_type === AgoraLinkTypeE.PRODUCT
  );
  const hasHiddenProductCatalog =
    catalogAddonActive && hasActiveProducts && (!productLink || productLink.is_hidden);

  // On mobile the launcher overlaps Agenda actions, the storefront editor's bottom
  // navigation and the sharing studio's sticky footer (it sits on top of «Compartir»).
  // Support remains available from the app menu and explicit ic_open links still work
  // through useIntercomAutoOpen.
  const showIntercom = !(
    (isMobileSize && isAgenda) ||
    (isMobileSize && isSharingStudio) ||
    (isStorefrontCompactSize && isMiPagina)
  );

  // The sharing studio brings its own header (back, step, progress): on the phone Flash's bar only
  // pushed the piece and the controls down.
  const fullScreen =
    path.includes('/bienvenido') ||
    (isMobileSize && isSharingStudio) ||
    (isMobileSize && path.includes('/ajustes/medios-de-cobro/mercadopago')) ||
    (isMobileSize && path.includes('/ajustes/medios-de-cobro/efectivo')) ||
    (isMobileSize && path.includes('/ajustes/medios-de-cobro/transferencia')) ||
    (isStorefrontCompactSize && isMiPagina);

  // Subscription lifecycle (already paused / trial inactive) still comes from
  // the session. Actionable payment states come exclusively from Cyclone's
  // canonical billing-alert resolver below.
  const lastVendorStatusHistory = session?.vendor?.vendor_status_history?.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0];

  const isChurn = lastVendorStatusHistory?.status === VendorHistoryStatusE.CHURN;
  const isInactive = lastVendorStatusHistory?.status === VendorHistoryStatusE.INACTIVE;

  // Determine which banner to show (priority order - payment/billing banners prioritized)
  const getBillingBannerState = (): BillingBannerState => {
    // 0. Un profesional del equipo no configura medios de cobro, ni paga el plan, ni edita
    // la página del negocio: todos estos banners lo mandan a pantallas donde no puede
    // hacer nada (y las lee del vendor del staff, que nunca tiene medios de cobro propios,
    // así que le gritaba "¡No hay ningún medio de cobro habilitado!" con el negocio OK).
    if (session?.is_staff) {
      return { show: false, type: 'warning' as const, message: '', action: undefined };
    }

    // 1a. Churn - highest priority (cancelled subscription)
    if (isChurn) {
      return {
        show: true,
        type: 'error' as const,
        message: 'Tu página se encuentra suspendida por falta de pago.',
        action: {
          label: 'Ir a pagar',
          onClick: () => navigate('/facturacion')
        }
      };
    }

    // 1b. Inactive - trial ended without subscribing
    if (isInactive) {
      return {
        show: true,
        type: 'error' as const,
        message: 'Período de prueba finalizado. Debes dar de alta tu plan mensual.',
        action: {
          label: 'Activar plan',
          onClick: () => navigate('/facturacion')
        }
      };
    }

    // 2. Canonical payment / renewal state. The banner and modal share the
    // same alert object; no date arithmetic or provider rules live in Flash.
    if (billingAlert && billingAlertBanner) {
      return {
        show: true,
        type: billingAlertBanner.variant,
        message: billingAlertBanner.message,
        action:
          billingAlert.actions.length > 0
            ? {
                label: 'Ver estado',
                onClick: () => openBillingAlert('banner')
              }
            : undefined
      };
    }

    // 4. Free trial ending (payment action needed soon)
    if (freeTrialValidation.shouldShowBanner) {
      // Error el último día, warning los dos anteriores
      const bannerType: BillingBannerState['type'] =
        freeTrialValidation.daysRemaining === 0 ? 'error' : 'warning';
      return {
        show: true,
        type: bannerType,
        message: freeTrialValidation.message,
        action: {
          label: 'Más info',
          onClick: () => navigate('/facturacion')
        }
      };
    }

    // 5. No payment methods enabled at all (clients can't book)
    const hasMP =
      session?.vendor?.payment_gateways?.includes(WalletTypeE.MERCADO_PAGO) &&
      !session?.vendor?.wallet_mercado_pago?.needs_reconnection;
    const hasStripe = session?.vendor?.payment_gateways?.includes(WalletTypeE.STRIPE);
    const hasCash = session?.vendor?.is_payment_cash_enabled;
    const hasBankTransfer = session?.vendor?.is_payment_bank_transfer_enabled;
    const isAcademyPlan = session?.vendor?.plan_name === 'academy';
    // Academy plan only supports MP and Stripe, not cash/transfer
    const hasNoPaymentMethods = isAcademyPlan
      ? !hasMP && !hasStripe
      : !hasMP && !hasStripe && !hasCash && !hasBankTransfer;

    if (hasNoPaymentMethods && session?.vendor?.is_onboarding_finished) {
      return {
        show: true,
        type: 'error' as const,
        message: isAcademyPlan
          ? '¡Conecta Mercado Pago para comenzar a vender!'
          : 'Tus reservas y ventas directas no tienen un medio de cobro habilitado.',
        action: {
          label: isAcademyPlan ? 'Ir ahora' : 'Habilitar',
          onClick: () => navigate('/ajustes/medios-de-cobro')
        }
      };
    }

    // 5b. Fecha límite de reservas ya pasada → agenda efectivamente cerrada,
    // mismo nivel que "sin método de pago": el negocio no puede recibir reservas nuevas.
    if (maxBookingDateValidation.isPastDate) {
      return {
        show: true,
        type: 'error' as const,
        message:
          'Tu agenda está cerrada: la fecha límite de reservas que configuraste ya pasó. Tus clientes no te pueden reservar.',
        action: {
          label: 'Extender fecha',
          onClick: () => navigate('/servicios/edicion-global/politicas')
        }
      };
    }

    // 6. Has unique events or online courses but no digital payment gateway (MP/Stripe)
    const hasDigitalGateway = hasMP || hasStripe;
    const hasActiveUniqueEvents =
      session?.vendor?.unique_events?.some((e) => e.status !== 'finished') ?? false;
    const hasOnlineCourses = session?.vendor?.online_courses_enabled;
    if (
      !hasDigitalGateway &&
      (hasActiveUniqueEvents || hasOnlineCourses) &&
      session?.vendor?.is_onboarding_finished
    ) {
      return {
        show: true,
        type: 'warning' as const,
        message: 'Conecta Mercado Pago para poder cobrar tus eventos y capacitaciones.',
        action: {
          label: 'Conectar',
          onClick: () => navigate('/ajustes/medios-de-cobro')
        }
      };
    }

    // 7. Missing payment methods for down payments (payment setup needed)
    if (shouldShowBanner) {
      return {
        show: true,
        type: 'warning' as const,
        message: `¡Urgente! No tienes ningún medio de pago configurado para el cobro de señas`,
        action: {
          label: 'Configurar',
          onClick: () => navigate('/ajustes/medios-de-cobro')
        }
      };
    }

    // 8. Mercado Pago needs reconnection (but other payment methods exist)
    if (session?.vendor?.wallet_mercado_pago?.needs_reconnection) {
      return {
        show: true,
        type: 'warning' as const,
        message: 'Tu cuenta de Mercado Pago necesita reconexión. Tus clientes no pueden pagar con MP.',
        action: {
          label: 'Reconectar',
          onClick: () => navigate('/ajustes/medios-de-cobro/mercadopago')
        }
      };
    }

    // 9. Has active gift-card templates but can't sell them (no usable MP wallet),
    // so they silently never render on the storefront. Lower priority than the
    // payment-method banners above — those already point the vendor to connect MP.
    if (hasOrphanedGiftCards && !canSellGiftCards) {
      return {
        show: true,
        type: 'warning' as const,
        message: 'Tus tarjetas de regalo no aparecen en tu página. Conectá Mercado Pago para venderlas.',
        action: {
          label: 'Conectar',
          onClick: () => navigate('/ajustes/medios-de-cobro')
        }
      };
    }

    // 9b. Tiene productos activos y el add-on pago, pero el botón de Productos está
    // oculto en su página → el catálogo no se ve. Baja prioridad, como el de gift
    // cards. CTA al editor de Mi Página, donde puede volver a mostrar el botón.
    if (hasHiddenProductCatalog) {
      return {
        show: true,
        type: 'warning' as const,
        message: 'Tu catálogo de productos no aparece en tu página. Mostrá el botón para venderlos.',
        action: {
          label: 'Mostrar',
          onClick: () => navigate('/mi-pagina')
        }
      };
    }

    // 10. Max booking date is set (LOWEST priority - informational only)
    if (maxBookingDateValidation.shouldShowBanner) {
      return {
        show: true,
        type: 'warning' as const,
        message: `Tus clientes pueden reservar hasta el ${maxBookingDateValidation.formattedDate}`,
        action: {
          label: 'Configurar',
          onClick: () => navigate('/servicios/edicion-global/politicas')
        }
      };
    }

    return { show: false, type: 'warning' as const, message: '', action: undefined };
  };

  const billingBannerState = getBillingBannerState();
  const bannerSettled = useBannerSettled(!!session);
  // Derivado en el render: antes pasaba por un efecto → contexto → segundo render y
  // el banner aparecía un frame tarde. El del shell le gana a cualquier banner
  // publicado por contexto, igual que antes; sin él, se muestra el del contexto.
  const shellBanner = billingBannerState.show ? billingBannerState : null;
  const globalBanner = !isOnboardingOrSetup && (
    <GlobalBanner
      variant={shellBanner ? shellBanner.type : bannerState.variant}
      message={shellBanner ? shellBanner.message : bannerState.message}
      isVisible={shellBanner ? true : bannerState.isVisible}
      action={shellBanner ? shellBanner.action : bannerState.action}
      dismissible={shellBanner ? false : bannerState.dismissible}
      pending={!bannerSettled}
      pinned={waPauseBanner && <WaPauseBanner banner={waPauseBanner} />}
    />
  );

  useEffect(() => {
    if (session) {
      identifyUser(session);
    }
  }, [session]);

  const hasFooter = !isAgenda && !isMiPagina && !isSharingStudio;

  const checklistReturnBanner = isReturningToChecklist ? (
    <Suspense fallback={null}>
      <ChecklistReturnBanner />
    </Suspense>
  ) : null;

  if (fullScreen) {
    return (
      <>
        {session && <IntercomBridge display={showIntercom} />}
        <FunctionSearchDialog enabled={!isOnboardingOrSetup} />
        {globalBanner}
        <main className="notranslate frost-ambient min-h-0 flex-1" id="main">
          <div className="relative z-[1] h-full flex flex-col">{children}</div>
        </main>
        {checklistReturnBanner}
      </>
    );
  }

  return (
    <>
      {session && <IntercomBridge display={showIntercom} />}
      <FunctionSearchDialog enabled={!isOnboardingOrSetup} />
      {globalBanner}
      {/* El fondo va en el contenedor y no en <main>: el riel flota con 12px de aire
          alrededor, y si sólo <main> llevara el fondo, esos 12px mostrarían el gris del
          body y se vería un corte vertical en el borde derecho del riel. */}
      <div className="frost-ambient flex flex-1 min-h-0">
        {isDesktop && <RailContainer />}
        <div className="flex min-w-0 flex-1 flex-col">
          {!isDesktop && <Header />}
          <main
            className={classNames(
              'notranslate min-h-0 flex-1',
              // Con footer, el main ocupa al menos el viewport: mientras la pantalla
              // carga, el footer queda abajo del pliegue en vez de pegado al borde
              // inferior y empujado después por el contenido (layout shift).
              hasFooter && 'lg:min-h-[calc(100vh-var(--app-banner-h,0px))]'
            )}
            id="main"
          >
            <div className="relative z-[1] h-full flex flex-col">{children}</div>
          </main>
          {hasFooter && isDesktop && (
            <Suspense fallback={null}>
              <Footer />
            </Suspense>
          )}
        </div>
      </div>
      {checklistReturnBanner}
    </>
  );
};

export const Layout: FunctionComponent<LayoutPropsI> = (props) => (
  <BillingAlertProvider>
    <LayoutShell {...props} />
    <Suspense fallback={null}>
      <AnnouncementsHost />
    </Suspense>
  </BillingAlertProvider>
);
